/**
 * How an item looks, everywhere it appears (2026-09-02).
 *
 * User: "work on the visual design for the shop and all items ... We also
 * need to visualize the stash."
 *
 * The shop and the stash were both lists of text rows — `Gilded Reliquary
 * RARE / 3.5kg` and a button. Nothing told you what you were holding, and
 * two screens that show the same objects showed them in two different
 * hand-rolled layouts.
 *
 * ## Where the art comes from
 *
 * Every icon is a render of the **actual prop the dungeon spawns**, not an
 * illustration of one: the same GLTF, the same texture atlas, the same
 * lighting. So the gilded reliquary in your stash is the gilded reliquary
 * you pulled out of the chest. That is also why the loot catalog was
 * renamed where it had to be (`item-defs.ts` header) — an icon that
 * contradicts its own label reads as a bug, and the packs ship no crown.
 *
 * Consumables deliberately reuse the ability plates they already have in
 * `/assets/ui/abilities/`. A tonic in the satchel shop and the same tonic in
 * the action bar are one object, and giving it two different pictures would
 * be a worse outcome than reusing one.
 *
 * String builders, not DOM builders, because every panel here renders with
 * `innerHTML` — and pure, so the rarity/labelling logic is testable without
 * a hub.
 *
 * Tailwind v4 extracts class names statically: a template hole inside a
 * class attribute produces no CSS rule at all. Every class below is a
 * literal, and the rarity variants are written out per branch rather than
 * interpolated.
 */

import type { LootRarity } from '../config/item-defs';
import { ITEM_DEFS, itemLabel } from '../config/item-defs';
import { isGearId } from '../config/gear-defs';
import { t, getIntlLocale } from '../i18n/i18n';

const ITEM_ICON_BASE = '/assets/ui/items';
const COSMETIC_ICON_BASE = '/assets/ui/cosmetics';
/** Weapons and shields, rendered from the very GLTFs the delver on the stage is holding. */
const GEAR_ICON_BASE = '/assets/ui/gear';
/** Consumables already have painted plates from the ability pass — same object, same picture. */
const CONSUMABLE_ICON_BASE = '/assets/ui/abilities';
/** Worn gear — the paperdoll's own plates (`ui/paperdoll.ts`). */
const WORN_ICON_BASE = '/assets/ui/worn';

/**
 * A carried item's picture — a curio, or a piece of worn gear (2026-10-15).
 *
 * QA 10.10 #8: a Tallow Cloak in the run bag drew a broken-image glyph. Gear
 * rides the carried-item pipeline (`gearAsCarried` on the server), so the bag
 * hands this a gear id, and `items/tallow_cloak.webp` never existed — the
 * cloak's plate is `worn/tallow_cloak.webp`, the one the paperdoll shows. One
 * id space, two folders; this is the only place that knows which.
 */
export function itemIconSrc(itemDefId: string): string {
  if (isGearId(itemDefId) && !ITEM_DEFS[itemDefId]) return `${WORN_ICON_BASE}/${itemDefId}.webp`;
  return `${ITEM_ICON_BASE}/${itemDefId}.webp`;
}

export function cosmeticIconSrc(cosmeticDefId: string): string {
  return `${COSMETIC_ICON_BASE}/${cosmeticDefId}.webp`;
}

/** `equipmentModel` is the roster's own filename — one name, one file, no second table. */
export function gearIconSrc(equipmentModel: string): string {
  return `${GEAR_ICON_BASE}/${equipmentModel}.webp`;
}

export function consumableIconSrc(consumableDefId: string): string {
  return `${CONSUMABLE_ICON_BASE}/${consumableDefId}.webp`;
}

/**
 * design/gdd/rites.md — Rite plates live beside the potion plates they sit
 * next to on the shelf, and are generated the same way (`a342fe3`).
 */
export function riteIconSrc(riteDefId: string): string {
  return `${CONSUMABLE_ICON_BASE}/${riteDefId}.webp`;
}

/**
 * The sigil an unpainted plate falls back to.
 *
 * `_unknown.webp` already exists and already means exactly this — it shipped
 * with the ability plates (`a342fe3`) for an ability whose art had not landed.
 * Reusing it means an unpainted Rite looks deliberate rather than broken.
 */
export const UNKNOWN_PLATE_SRC = `${CONSUMABLE_ICON_BASE}/_unknown.webp`;

/**
 * An `onerror` attribute that swaps a missing plate for the unknown sigil
 * (2026-10-15). For the hand-rolled `<img>`s outside `itemTileMarkup` — the
 * run bag, the loot toast, the stash — so a future id without art reads as
 * "unpainted", never as the browser's broken-image glyph. Removes itself
 * first, so a missing fallback cannot loop.
 */
export const ICON_FALLBACK_ATTR = `onerror="this.onerror=null;this.src='${UNKNOWN_PLATE_SRC}'"`;

/** Escapes anything heading into innerHTML. Ids are ours; labels are derived from them, and this is still cheaper than being wrong. */
export function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

/**
 * Rarity is carried by the frame, not by a coloured word.
 *
 * A grid is scanned, not read: at a glance you should see which two tiles
 * are worth something. The text label stays for the same reason the quest
 * bar kept its numbers — colour alone is not readable to everyone, and the
 * gold frame is doing double duty as decoration.
 */
export function rarityFrameClass(rarity: LootRarity): string {
  return rarity === 'RARE'
    ? 'border-gold/70 bg-[radial-gradient(circle_at_50%_35%,rgba(224,166,64,0.22),transparent_70%)]'
    : 'border-border bg-black/20';
}

export function rarityTextClass(rarity: LootRarity): string {
  return rarity === 'RARE' ? 'text-gold' : 'text-text-dim';
}

export interface ItemTileOptions {
  src: string;
  alt: string;
  rarity?: LootRarity;
  /** Small badge in the corner — a carried count, or charges owned. */
  badge?: string;
  /** Dimmed and desaturated: owned-but-unavailable, or unaffordable. */
  muted?: boolean;
  /**
   * Makes the picture itself open the item's detail sheet.
   *
   * The picture rather than the whole card, because every card already has a
   * button on it that spends money — one press must not be able to mean either
   * "tell me about this" or "buy it" depending on where the thumb landed. The
   * tile is the largest target on the card and the obvious thing to prod.
   *
   * A full attribute string (`data-detail="curio:ale_keg"`), so a caller picks
   * its own attribute name and this file learns nothing about shelves.
   */
  detailAttr?: string;
  /**
   * What to show if `src` does not load.
   *
   * A missing plate is not an exceptional case here: art lands after code by
   * design (`design/gdd/asset-generation.md`), so a shelf ships describing
   * items whose pictures do not exist yet, and a browser's broken-image glyph
   * plus the alt text is the worst possible version of that — it reads as a
   * bug in the shop rather than as a plate that has not been painted.
   *
   * The handler removes itself before swapping, so a fallback that is also
   * missing cannot loop.
   */
  fallbackSrc?: string;
}

/** The square picture itself. Shared by the shop, the stash and the satchel so one object never has two looks. */
export function itemTileMarkup(options: ItemTileOptions): string {
  const frame = rarityFrameClass(options.rarity ?? 'COMMON');
  const dim = options.muted ? 'opacity-50 grayscale' : '';
  const badge = options.badge
    ? `<span class="absolute bottom-1 right-1 px-1.5 rounded-full bg-bg/85 border border-border text-[10px] text-text tabular-nums">${esc(options.badge)}</span>`
    : '';
  // Every tile falls back now, not only the ones that asked (2026-10-15):
  // a broken-image glyph is never the right answer, whatever the shelf.
  const fallback = options.fallbackSrc ?? UNKNOWN_PLATE_SRC;
  const onError = ` onerror="this.onerror=null;this.src='${esc(fallback)}'"`;
  const inner = `
      <img src="${esc(options.src)}" alt="${esc(options.alt)}" loading="lazy"${onError}
           class="absolute inset-0 w-full h-full object-contain p-1.5 ${dim}" />
      ${badge}`;
  if (options.detailAttr) {
    return `
    <button type="button" ${options.detailAttr} aria-label="${esc(t('What is {name}?', { name: options.alt }))}"
      class="relative block w-full aspect-square rounded-lg border-2 ${frame} overflow-hidden
             hover:border-gold/70 focus-visible:border-gold">${inner}
    </button>
  `;
  }
  return `
    <div class="relative aspect-square rounded-lg border-2 ${frame} overflow-hidden">${inner}
    </div>
  `;
}

export interface ItemCardOptions extends ItemTileOptions {
  title: string;
  /** One short line under the title — weight, class, what it does. */
  subtitle?: string;
  /** The button or status pill. Already-built markup, because callers own their own actions. */
  action?: string;
}

/** Picture, name, one line of detail, one control. Every grid on the Shop and Stash screens is made of these. */
export function itemCardMarkup(options: ItemCardOptions): string {
  const subtitle = options.subtitle
    ? `<p class="text-[11px] text-text-dim truncate">${esc(options.subtitle)}</p>`
    : '';
  return `
    <div class="flex flex-col gap-1.5 p-2 rounded-lg bg-surface-2 border border-border">
      ${itemTileMarkup(options)}
      <div class="min-w-0">
        <p class="text-[13px] truncate">${esc(options.title)}</p>
        ${subtitle}
      </div>
      ${options.action ?? ''}
    </div>
  `;
}

/** The capitalised rarity word the loot cards print, in the player's language. */
function rarityCaps(rarity: LootRarity): string {
  return rarity === 'RARE' ? t('RARE') : t('COMMON');
}

/** "3.5kg · RARE" — the two facts a loot item is judged on, in one line. */
export function itemSubtitle(itemDefId: string, rarity: LootRarity): string {
  const def = ITEM_DEFS[itemDefId];
  const label = rarityCaps(rarity);
  if (!def) return label;
  const kg = def.weightKg.toLocaleString(getIntlLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return t('{kg}kg · {rarity}', { kg, rarity: label });
}

/** Falls back to the prettified id, so an item the client mirror has not heard of still names itself. */
export function itemTitle(itemDefId: string): string {
  return itemLabel(itemDefId);
}
